import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import BoltIcon from '@mui/icons-material/Bolt';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { buildComparatorLink } from '../../utils/queryParams';
import { formatCAD } from '../../utils/formatCurrency';
import { trackInsightImpression, trackInsightClick } from './insightAnalytics';
import { useBudget } from '../../context/BudgetContext';
import CustomIcon from '../../../../components/CustomIcon';

export default function InsightCard({ rule, priority = 'supporting' }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const { state, derived } = useBudget();
  const isCustomIcon = typeof rule.icon === 'string';
  const Icon = isCustomIcon ? null : rule.icon;

  useEffect(() => {
    trackInsightImpression(rule.id);
  }, [rule.id]);

  const ctaLink = buildComparatorLink(lng, rule.ctaPath, rule.ctaParams || {});

  // Compute annual impact if rule defines it
  let annualImpact = null;
  if (rule.annualImpact) {
    try {
      annualImpact = typeof rule.annualImpact === 'function'
        ? rule.annualImpact(state, derived)
        : null;
    } catch { /* ignore */ }
  }

  // Interpolate body text with dynamic values
  let bodyText = t(rule.bodyKey, rule.bodyDefault);
  if (rule.id === 'no-emergency-fund') {
    const months = Math.floor(derived.currentEmergencyMonths * 10) / 10;
    bodyText = bodyText.replace('{{months}}', String(months));
  }

  const isEmotional = rule.emotional;
  const isHighImpact = rule.priority <= 2;
  const isQuickWin = rule.priority >= 3 && annualImpact != null && annualImpact > 0;

  return (
    <Card
      elevation={priority === 'supporting' ? 0 : 1}
      sx={{
        border: isEmotional ? '1px solid #FFEBEE' : priority === 'supporting' ? '1px solid #EEF0F3' : '1px solid #E6E9EE',
        borderLeft: priority === 'primary' ? '4px solid #1E3A5F' : undefined,
        bgcolor: isEmotional ? '#FFEBEE' : '#fff',
        p: priority === 'supporting' ? 0.5 : undefined,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': { borderColor: '#1E3A5F', boxShadow: 3, transform: 'translateY(-2px)' },
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ color: isEmotional ? '#C62828' : '#1E3A5F', mt: 0.5, fontSize: 32 }}>
            {isCustomIcon ? (
              <CustomIcon name={rule.icon} size={36} />
            ) : (
              <Icon fontSize="inherit" />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            {/* Primary recommendation label */}
            {priority === 'primary' && (
              <Chip
                label={t('budgetPlanner.insights.primaryRec', 'Recomendación principal')}
                size="small"
                sx={{
                  height: 20,
                  bgcolor: '#1E3A5F',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  mb: 0.75,
                }}
              />
            )}
            {/* Category + impact badges row */}
            <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
              {isHighImpact && (
                <Chip
                  icon={<WhatshotIcon sx={{ fontSize: '14px !important' }} />}
                  label={t('budgetPlanner.insights.highImpact', 'Alto impacto')}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: '#FFF8E1',
                    color: '#7D5A00',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': { color: '#7D5A00' },
                  }}
                />
              )}
              {isQuickWin && (
                <Chip
                  icon={<BoltIcon sx={{ fontSize: '14px !important' }} />}
                  label={t('budgetPlanner.insights.quickWin', 'Ganancia rápida')}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: '#E8F5E9',
                    color: '#2E7D32',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': { color: '#2E7D32' },
                  }}
                />
              )}
              {annualImpact != null && annualImpact > 0 && (
                <Chip
                  label={t('budgetPlanner.insights.annualImpact', '~{{amount}}/año', { amount: formatCAD(annualImpact) })}
                  size="small"
                  sx={{
                    height: 22,
                    bgcolor: rule.id === 'good-savings' ? '#E8F5E9' : '#FFF8E1',
                    color: rule.id === 'good-savings' ? '#2E7D32' : '#7D5A00',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Box>

            <Typography
              variant={priority === 'primary' ? 'h6' : 'subtitle1'}
              fontWeight={600}
              sx={{ mb: 0.5, fontSize: priority === 'supporting' ? '0.875rem' : undefined }}
            >
              {t(rule.titleKey, rule.titleDefault)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontSize: priority === 'supporting' ? '0.8rem' : undefined }}
            >
              {bodyText}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to={ctaLink}
                variant={priority === 'supporting' ? 'text' : priority === 'secondary' ? 'outlined' : 'contained'}
                size="small"
                onClick={() => trackInsightClick(rule.id, rule.ctaPath)}
                sx={{
                  ...(priority === 'primary' && { bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#1A3152' } }),
                  ...(priority === 'secondary' && { borderColor: '#1E3A5F', color: '#1E3A5F' }),
                  ...(priority === 'supporting' && { color: '#1E3A5F', fontSize: '0.8rem', p: 0, minWidth: 'auto' }),
                  textTransform: 'none',
                }}
              >
                {t(rule.ctaLabelKey, rule.ctaLabelDefault)}
              </Button>
              {rule.secondaryPath && (
                <Button
                  component={RouterLink}
                  to={`/${lng}/${rule.secondaryPath}`}
                  variant="outlined"
                  size="small"
                  sx={{ borderColor: '#1E3A5F', color: '#1E3A5F', textTransform: 'none' }}
                >
                  {t(rule.secondaryLabelKey, rule.secondaryLabelDefault)}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
